// The per-post AIP signature domain, built by Twetch's own WASM.
//
// A sponsored post is AUTHORED by the user's wallet: the wallet signs the
// AIP domain (`0x6a || pushdata parts`) and the paymaster funds the miner
// fee. The domain bytes MUST match what the server rebuilds from the
// structured fields, so this app does not re-implement the encoding — it
// loads the API's own wasm-bindgen module (`/wasm/twetch_crypto.js`) at
// runtime and calls `postAipMessageV2`, the same function Twetch's client
// uses. The module is fetched from api.twetch.com via native dynamic import
// (webpackIgnore keeps Next from trying to bundle a remote URL).
//
// THE UTF-8 CAVEAT (mirrors Twetch's unisat adapter): wallets sign STRINGS —
// their UTF-8 bytes. Our domain is raw bytes. Valid UTF-8 has a unique
// encoding, so strict-decode → sign → the wallet's re-encode is
// byte-identical; if strict decode FAILS we refuse to sign rather than let
// the wallet sign different bytes than the server verifies.

import { TWETCH_API } from './twetch-api'
import { hexToBytes, utf8DecodeStrict } from './twetch-crypto'

const AIP_BINARY_MESSAGE_ERROR =
	"This post contains characters a BTC wallet can't sign. Edit the post and try again."

interface TwetchCryptoModule {
	default: (moduleOrPath?: string | { module_or_path: string }) => Promise<unknown>
	postAipMessage: (
		content: string,
		replyTx?: string,
		twdata?: string,
		mediaRefs?: string,
		tsMs?: bigint
	) => string
	postAipMessageV2: (
		content: string,
		replyTx?: string,
		twdata?: string,
		mediaRefs?: string,
		tagRefs?: string,
		agentPubkey?: string,
		tsMs?: bigint
	) => string
}

let modulePromise: Promise<TwetchCryptoModule> | null = null

function loadTwetchCrypto(): Promise<TwetchCryptoModule> {
	if (!modulePromise) {
		modulePromise = (async () => {
			const mod = (await import(
				/* webpackIgnore: true */ `${TWETCH_API}/wasm/twetch_crypto.js`
			)) as TwetchCryptoModule
			await mod.default({ module_or_path: `${TWETCH_API}/wasm/twetch_crypto_bg.wasm` })
			return mod
		})()
		// A failed load must not poison every later attempt (transient network).
		modulePromise.catch(() => {
			modulePromise = null
		})
	}
	return modulePromise
}

/// The exact text the wallet signs for a sponsored post: the AIP domain as a
/// strictly-decoded UTF-8 string. `tsMs` is the signed replay-guard timestamp
/// and MUST be echoed verbatim in the request's `aip.ts`.
export async function postAipText(
	content: string,
	replyTx: string | null,
	tsMs: number,
	metadata: {
		tagRefs?: string[]
		agentPubkey?: string | null
		twdata?: string | null
	} = {}
): Promise<string> {
	const mod = await loadTwetchCrypto()
	const hex = mod.postAipMessageV2(
		content,
		replyTx ?? undefined,
		metadata.twdata ?? undefined,
		undefined,
		metadata.tagRefs?.length ? JSON.stringify(metadata.tagRefs) : undefined,
		metadata.agentPubkey ?? undefined,
		BigInt(tsMs)
	)
	const text = utf8DecodeStrict(hexToBytes(hex))
	if (text === null) {
		throw new Error(AIP_BINARY_MESSAGE_ERROR)
	}
	return text
}
